import React, { useState, useEffect } from 'react';
import { ChevronDown, Save, Palmtree, Calendar, Plus, Trash2, X, CalendarDays } from 'lucide-react';
import { attendanceAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth } from './AuthContext';
import DateInput from './DateInput';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const HolidaySchedule = () => {
  const { user } = useAuth();
  const isMember = user?.role === 'Member';
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: '',
    type: 'public',
    optional: false
  });

  const holidayTypes = [
    { value: 'public', label: 'Public Holiday', color: '#dbeafe', textColor: '#1e40af' },
    { value: 'company', label: 'Company Holiday', color: '#fef3c7', textColor: '#92400e' },
    { value: 'festive', label: 'Festive Holiday', color: '#fce7f3', textColor: '#9f1239' },
    { value: 'religious', label: 'Religious Holiday', color: '#e0e7ff', textColor: '#3730a3' },
    { value: 'regional', label: 'Regional Holiday', color: '#f8f9fa', textColor: '#374151' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i - 1);

  // Fetch holidays from database when component mounts or year changes
  useEffect(() => {
    const fetchHolidays = async () => {
      setLoading(true);
      try {
        const response = await attendanceAPI.getHolidays(year);
        console.log('Fetched holidays:', response);

        // Transform API response to component format
        const fetchedHolidays = (response?.holidays || response || []).map(holiday => ({
          id: holiday.id,
          name: holiday.holiday_name || holiday.holidayName,
          date: holiday.holiday_date || holiday.holidayDate,
          type: holiday.holiday_type || holiday.holidayType || 'public',
          optional: holiday.optional || false
        }));

        setHolidays(fetchedHolidays);
      } catch (error) {
        console.error('Error fetching holidays:', error);
        setHolidays([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHolidays();
  }, [year]);

  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      alert('Please enter holiday name and date');
      return;
    }

    try {
      const holidayData = {
        year: parseInt(year),
        holidayName: newHoliday.name,
        holidayDate: newHoliday.date,
        holidayType: newHoliday.type,
        optional: newHoliday.optional
      };

      await attendanceAPI.addHolidays(holidayData);

      // Refetch holidays from database
      const response = await attendanceAPI.getHolidays(year);
      const fetchedHolidays = (response?.holidays || response || []).map(holiday => ({
        id: holiday.id,
        name: holiday.holiday_name || holiday.holidayName,
        date: holiday.holiday_date || holiday.holidayDate,
        type: holiday.holiday_type || holiday.holidayType || 'public',
        optional: holiday.optional || false
      }));
      setHolidays(fetchedHolidays);

      // Reset form and close modal
      setNewHoliday({ name: '', date: '', type: 'public', optional: false });
      setShowModal(false);
      notifySavedAndReload('✓ Holiday added successfully!');
    } catch (error) {
      console.error('Error adding holiday:', error);
      alert('❌ Error adding holiday: ' + error.message);
    }
  };

  const handleRemoveHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    try {
      await attendanceAPI.deleteHoliday(id);
      setHolidays(holidays.filter(h => h.id !== id));
      notifySavedAndReload('✓ Holiday deleted successfully!');
    } catch (error) {
      console.error('Error deleting holiday:', error);
      alert('❌ Error deleting holiday: ' + error.message);
    }
  };

  const sortedHolidays = [...holidays].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Group holidays by month
  const groupedHolidays = sortedHolidays.reduce((acc, holiday) => {
    const month = new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'long' });
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(holiday);
    return acc;
  }, {});

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', padding: '0.5rem 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Palmtree size={20} color="#4F46E5" />
            <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              Holiday Schedule {year}
            </h2>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
              ({sortedHolidays.length} holidays)
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {/* Year Selector */}
            <div style={{ position: 'relative' }}>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                style={{
                  padding: '0.375rem 2.25rem 0.375rem 1rem',
                  minWidth: '120px',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  appearance: 'none',
                  background: 'var(--theme-surface, #ffffff)'
                }}
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <ChevronDown size={14} style={{
                position: 'absolute',
                right: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280'
              }} />
            </div>

            {/* Add Holiday Button */}
            {!isMember && (
              <button
                onClick={() => setShowModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.875rem',
                  background: 'linear-gradient(135deg, var(--theme-primary, #4F46E5) 0%, var(--theme-primaryDark, #3730A3) 100%)',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(79, 70, 229, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(79, 70, 229, 0.2)';
                }}
              >
                <Plus size={16} />
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {/* Holidays List */}
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '0.75rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              color: '#9ca3af'
            }}>
              <Calendar size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p style={{ fontSize: '0.75rem' }}>Loading holidays...</p>
            </div>
          ) : sortedHolidays.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {Object.entries(groupedHolidays).map(([month, monthHolidays]) => (
                <div key={month}>
                  <h3 style={{
                    fontSize: '0.8125rem',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '0.375rem',
                    paddingBottom: '0.25rem',
                    borderBottom: '1.5px solid #e5e7eb'
                  }}>
                    {month} ({monthHolidays.length})
                  </h3>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.25rem'
                  }}>
                    {monthHolidays.map((holiday) => {
                      const holidayType = holidayTypes.find(t => t.value === holiday.type);
                      return (
                        <div key={holiday.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem',
                          background: 'var(--theme-surface, #ffffff)',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          transition: 'all 0.2s',
                          cursor: 'default',
                          position: 'relative',
                          minWidth: '180px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = 'var(--theme-primary, #4F46E5)';
                          e.currentTarget.style.boxShadow = '0 1px 4px rgba(79, 70, 229, 0.15)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}>
                          {/* Delete Button */}
                          {!isMember && (
                            <button
                              onClick={() => handleRemoveHoliday(holiday.id)}
                              style={{
                                position: 'absolute',
                                top: '0.25rem',
                                right: '0.25rem',
                                padding: '0.25rem',
                                borderRadius: '0.25rem',
                                border: 'none',
                                background: '#fee2e2',
                                color: '#dc2626',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'background 0.2s',
                                opacity: 0.6
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = '#fecaca';
                                e.currentTarget.style.opacity = '1';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = '#fee2e2';
                                e.currentTarget.style.opacity = '0.6';
                              }}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}

                          {/* Date Badge */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            borderRadius: '0.375rem',
                            background: 'linear-gradient(135deg, var(--theme-primary, #4F46E5) 0%, var(--theme-primaryDark, #3730A3) 100%)',
                            color: 'var(--theme-surface, #ffffff)',
                            flexDirection: 'column',
                            flexShrink: 0,
                            boxShadow: '0 1px 3px rgba(79, 70, 229, 0.3)'
                          }}>
                            <div style={{ fontSize: '0.9375rem', fontWeight: '700', lineHeight: 1 }}>
                              {new Date(holiday.date + 'T00:00:00').getDate()}
                            </div>
                            <div style={{ fontSize: '0.5rem', fontWeight: '600', textTransform: 'uppercase', marginTop: '0.125rem' }}>
                              {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' })}
                            </div>
                          </div>

                          <div style={{ flex: 1, minWidth: 0, paddingRight: '1.25rem' }}>
                            {/* Holiday Name */}
                            <h4 style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#1f2937',
                              marginBottom: '0.125rem',
                              lineHeight: '1.2',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={holiday.name}>
                              {holiday.name}
                            </h4>

                            {/* Day of Week */}
                            <p style={{
                              fontSize: '0.625rem',
                              color: '#6b7280',
                              marginBottom: '0.25rem',
                              lineHeight: 1
                            }}>
                              {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' })}
                            </p>

                            {/* Tags */}
                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                              <span style={{
                                padding: '0.125rem 0.375rem',
                                borderRadius: '9999px',
                                fontSize: '0.5625rem',
                                fontWeight: '600',
                                background: holidayType?.color || '#f8f9fa',
                                color: holidayType?.textColor || '#374151',
                                lineHeight: 1.2
                              }}>
                                {holidayType?.label}
                              </span>
                              {holiday.optional && (
                                <span style={{
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.5625rem',
                                  fontWeight: '600',
                                  background: '#f3e8ff',
                                  color: '#6b21a8',
                                  lineHeight: 1.2
                                }}>
                                  Optional
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <CalendarDays size={40} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                No holidays scheduled
              </h3>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                {isMember
                  ? `No holidays have been published for ${year} yet.`
                  : `Start by adding your first holiday for ${year}`}
              </p>
              {!isMember && (
                <button
                  onClick={() => setShowModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, var(--theme-primary, #4F46E5) 0%, var(--theme-primaryDark, #3730A3) 100%)',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <Plus size={16} />
                  Add Holiday
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Holiday Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          animation: 'fadeIn 0.2s ease-out'
        }}
        onClick={() => setShowModal(false)}>
          <div style={{
            background: 'var(--theme-surface, #ffffff)',
            borderRadius: '1rem',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'hidden',
            animation: 'slideUp 0.3s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, var(--theme-primary, #4F46E5) 0%, var(--theme-primaryDark, #3730A3) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 6px rgba(79, 70, 229, 0.3)'
                }}>
                  <Plus size={24} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                    Add New Holiday
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                    Add a holiday to {year} calendar
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.15s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.color = '#1f2937';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Holiday Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Holiday Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newHoliday.name}
                    onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                    placeholder="e.g., Independence Day"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.9375rem',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--theme-primary, #4F46E5)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Date */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Date <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <DateInput
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                    className="update-employee-input"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.9375rem'
                    }}
                  />
                </div>

                {/* Holiday Type */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Holiday Type
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={newHoliday.type}
                      onChange={(e) => setNewHoliday({ ...newHoliday, type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.9375rem',
                        appearance: 'none',
                        cursor: 'pointer',
                        background: 'var(--theme-surface, #ffffff)'
                      }}
                    >
                      {holidayTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={18} style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} />
                  </div>
                </div>

                {/* Optional Checkbox */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--theme-surface)',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <input
                    type="checkbox"
                    id="optional-holiday"
                    checked={newHoliday.optional}
                    onChange={(e) => setNewHoliday({ ...newHoliday, optional: e.target.checked })}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginRight: '0.75rem',
                      cursor: 'pointer',
                      accentColor: 'var(--theme-primary, #4F46E5)'
                    }}
                  />
                  <label htmlFor="optional-holiday" style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer',
                    margin: 0
                  }}>
                    Mark as Optional Holiday
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              padding: '1.25rem 1.5rem',
              borderTop: '1px solid #e5e7eb',
              background: 'var(--theme-surface)'
            }}>
              <button
                onClick={() => {
                  setNewHoliday({ name: '', date: '', type: 'public', optional: false });
                  setShowModal(false);
                }}
                style={{
                  padding: '0.625rem 1.25rem',
                  border: '1.5px solid #d1d5db',
                  background: 'var(--theme-surface, #ffffff)',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--theme-surface, #ffffff)'}
              >
                Cancel
              </button>
              <button
                onClick={handleAddHoliday}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.5rem',
                  background: 'linear-gradient(135deg, var(--theme-primary, #4F46E5) 0%, var(--theme-primaryDark, #3730A3) 100%)',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px rgba(79, 70, 229, 0.2)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(79, 70, 229, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(79, 70, 229, 0.2)';
                }}
              >
                <Plus size={18} />
                Add Holiday
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default HolidaySchedule;
